import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { Billing } from './models';
import { baseApi } from '@centerpiece/components';

export const billing = baseApi
  .enhanceEndpoints({ addTagTypes: ['Billings', 'Billing'] })
  .injectEndpoints({
    endpoints: builder => ({
      getBillings: builder.query<Billing[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'billing',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({ type: 'Billing' as const, id })),
                'Billings',
              ]
            : ['Billings'],
      }),
      getBilling: builder.query<Billing, string>({
        query: id => ({
          url: `billing/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [{ type: 'Billing', id: arg }],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createBilling: builder.mutation({
        query: request => ({
          url: 'billing',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['Billings'],
      }),
      updateBilling: builder.mutation<Billing, Billing>({
        query: request => ({
          url: `billing/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'Billing', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            billing.util.updateQueryData('getBilling', `${id}`, draft => {
              Object.assign(draft, patch);
            })
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(billing.util.invalidateTags(['Billings']))
             */
            dispatch(billing.util.invalidateTags(['Billings']));
          }
        },
      }),
      deleteBilling: builder.mutation<Billing, number>({
        query: id => ({
          url: `billing/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'Billing' as const, arg }, 'Billings']
            : ['Billings'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetBillingsQuery,
  useGetBillingQuery,
  useCreateBillingMutation,
  useUpdateBillingMutation,
  useDeleteBillingMutation,
  usePrefetch: useBillingsPrefetch,
} = billing;
const selectBillingsRoot = billing.endpoints.getBillings.select({});
export const selectBillings = createSelector(
  selectBillingsRoot,
  billing => billing.data || []
);
